import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, ChevronRight, Award, Clock, Trophy } from "lucide-react";
import { LessonBlockRenderer } from "@/components/LessonBlocks";

export default function CourseLearn() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/courses/${id}/learn`);
      setCourse(r.data.course);
      setEnrollment(r.data.enrollment);
      if (!r.data.enrollment) {
        toast.error("You're not enrolled — redirecting to course page");
        navigate(`/dashboard/courses/${id}`);
        return;
      }
      // pick first incomplete lesson by default
      const completed = new Set(r.data.enrollment.lessons_completed || []);
      const firstIncomplete = (r.data.course.modules || []).flatMap(m => m.lessons || [])
        .find(l => !completed.has(l.id));
      const first = firstIncomplete || (r.data.course.modules?.[0]?.lessons?.[0]);
      if (first) setActiveLessonId(first.id);
    } catch (e) { toast.error("Could not load course"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const lessonFlat = useMemo(() => {
    if (!course) return [];
    return (course.modules || []).flatMap(m => (m.lessons || []).map(l => ({ ...l, module: m })));
  }, [course]);

  const activeLesson = useMemo(() => lessonFlat.find(l => l.id === activeLessonId), [lessonFlat, activeLessonId]);
  const completedSet = useMemo(() => new Set(enrollment?.lessons_completed || []), [enrollment]);
  const progress = enrollment?.progress || 0;

  const submitQuizBlock = async (blockId, answer) => {
    setSubmittingQuiz(true);
    try {
      const r = await api.post(`/courses/${id}/lessons/${activeLessonId}/quiz`, {
        answers: { [blockId]: answer },
      });
      return r.data.block_results?.[blockId];
    } catch (e) {
      toast.error("Submit failed");
      return null;
    } finally { setSubmittingQuiz(false); }
  };

  const markLessonComplete = async () => {
    try {
      const r = await api.post(`/courses/${id}/lessons/${activeLessonId}/complete`);
      setEnrollment(r.data);
      toast.success(r.data.progress >= 100 ? "Course completed! Generating certificate…" : "Lesson marked complete");
      // advance to next incomplete
      const idx = lessonFlat.findIndex(l => l.id === activeLessonId);
      const next = lessonFlat.slice(idx + 1).find(l => !new Set(r.data.lessons_completed || []).has(l.id));
      if (next) setActiveLessonId(next.id);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not mark complete";
      toast.error(msg);
    }
  };

  const goToCertificate = () => navigate(`/dashboard/courses/${id}/certificate`);

  if (!course || !enrollment) return <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>;

  return (
    <div data-testid="course-learn-page">
      <Link to="/dashboard/courses" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> COURSES
      </Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{course.level} · {course.duration_hours}h</div>
          <h1 className="font-serif text-2xl lg:text-3xl text-kb-ink mt-1 leading-tight">{course.title}</h1>
        </div>
        <div className="text-right">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">PROGRESS</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-40 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-kb-primary" style={{ width: `${progress}%` }} />
            </div>
            <span className="font-mono text-sm text-kb-primary">{progress}%</span>
          </div>
          {enrollment.certificate_id && (
            <button onClick={goToCertificate} className="kb-btn-primary text-xs mt-3" data-testid="view-certificate-btn">
              <Award className="w-3.5 h-3.5" /> View certificate
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        {/* Sidebar: lessons list */}
        <aside className="lg:col-span-4 kb-card p-5 self-start" data-testid="learn-sidebar">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CURRICULUM</div>
          <div className="mt-3 space-y-4">
            {(course.modules || []).map((m, mi) => (
              <div key={m.id}>
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary mb-2">MOD {String(mi+1).padStart(2,'0')} · {m.title}</div>
                <ul className="space-y-1">
                  {(m.lessons || []).map((l) => {
                    const done = completedSet.has(l.id);
                    const active = l.id === activeLessonId;
                    return (
                      <li key={l.id}>
                        <button onClick={() => setActiveLessonId(l.id)}
                          className={`w-full text-left text-sm flex items-center gap-2 px-2 py-2 rounded-sm transition-colors ${
                            active ? "bg-kb-primary/10 text-kb-primary" :
                            done ? "text-kb-ink-muted hover:bg-slate-50" :
                            "text-kb-ink hover:bg-slate-50"
                          }`}
                          data-testid={`lesson-link-${l.id}`}>
                          {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Circle className="w-3.5 h-3.5 text-kb-ink-light shrink-0" />}
                          <span className="flex-1 line-clamp-1">{l.title}</span>
                          <span className="kb-mono text-[10px] text-kb-ink-light shrink-0">{l.duration_min}m</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* Lesson body */}
        <section className="lg:col-span-8 space-y-5" data-testid="learn-body">
          {!activeLesson ? (
            <div className="kb-card p-6 text-sm text-kb-ink-muted">Select a lesson from the left to begin.</div>
          ) : (
            <>
              <div className="kb-card p-6">
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                  {activeLesson.module.title} · <Clock className="w-3 h-3 inline" /> {activeLesson.duration_min}m
                </div>
                <h2 className="font-serif text-2xl text-kb-ink mt-2">{activeLesson.title}</h2>
              </div>

              {(activeLesson.blocks || []).length === 0 ? (
                <div className="kb-card p-6 text-sm text-kb-ink-muted text-center">This lesson has no content blocks yet.</div>
              ) : (
                <div className="space-y-5">
                  {activeLesson.blocks.map((b) => (
                    <div key={b.id} className="kb-card p-6">
                      <LessonBlockRenderer block={b} courseId={course.id} lessonId={activeLesson.id}
                        currentUser={user} onQuizSubmit={submitQuizBlock} attempted={submittingQuiz} />
                    </div>
                  ))}
                </div>
              )}

              <div className="kb-card p-5 flex flex-wrap items-center justify-between gap-3">
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                  {completedSet.has(activeLesson.id) ? "Lesson complete · revisit any time" : "Done with this lesson?"}
                </div>
                {!completedSet.has(activeLesson.id) && (
                  <button onClick={markLessonComplete} className="kb-btn-primary text-sm" data-testid="lesson-mark-complete">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark complete & continue
                  </button>
                )}
                {progress >= 100 && (
                  <button onClick={goToCertificate} className="kb-btn-primary text-sm" data-testid="learn-cert-btn">
                    <Trophy className="w-3.5 h-3.5" /> Get your certificate
                  </button>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
